import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Polyline, Polygon, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from 'leaflet';
import { useDataContext } from "../../context/DataContext";

const LegendControl = () => {
  const [legendAdded, setLegendAdded] = useState(false);
  const map = useMap();

  useEffect(() => {
    if (!legendAdded) {
      const legend = L.control({ position: 'bottomright' });

      legend.onAdd = function (map) {
        const div = L.DomUtil.create('div', 'mapLegend');
        div.innerHTML = `
          <div style="background-color: #eeeeeeb0;
          padding: 0px 10px 10px;
          border: 1px solid #80808042;">
            <div style="margin-bottom: 10px;">
              <p style="letter-spacing: normal; margin-bottom: 5px; font-size: 15px; font-family: 'Montserrat', sans-serif; color: black;">Strada Selezionata:</p>
              <div style="background-color: #FF6B00; width: 30px; height: 5px;"></div>
            </div>
            <div style="margin-bottom: 10px;">
              <p style="letter-spacing: normal; margin-bottom: 5px; font-size: 15px; font-family: 'Montserrat', sans-serif; color: black;">Coda Max:</p>
              <div style="background-color: #EC1D23; width: 30px; height: 5px;"></div>
            </div>
            <div>
              <p style="letter-spacing: normal; margin-bottom: 5px; font-size: 15px; font-family: 'Montserrat', sans-serif; color: black;">Coda Media:</p>
              <div style="background-color: #FDB64E; width: 30px; height: 5px;"></div>
            </div>
          </div>
        `;
        return div;
      };

      legend.addTo(map);
      setLegendAdded(true);
    }
  }, [legendAdded, map]);

  return null;
};

const SectionMap = ({ selectedIdScenario, selectedSectionId, selectedSectionName }) => {
  const [dataJson, setDataJson] = useState({});
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedRoadIndex, setSelectedRoadIndex] = useState(null);

  // Use DataContext for client-specific data (default or lavagna)
  const { dataJson: contextDataJson, dataLoaded } = useDataContext();

  useEffect(() => {
    if (dataLoaded && contextDataJson && selectedIdScenario) {
      const selectedScenario = contextDataJson.scenario?.find(
        scenario => scenario.replicaAvg === selectedIdScenario
      );
      if (selectedScenario) {
        setDataJson(selectedScenario);
      } else {
        setDataJson({});
      }
    }
  }, [selectedIdScenario, contextDataJson, dataLoaded]);

  // Try to find which node and road index corresponds to the selected section
  useEffect(() => {
    if (selectedSectionName && dataJson.nodiAimsun) {
      let found = false;

      // Create a mapping table based on common patterns in Italian road names
      const sectionNameToNodeMapping = {
        "Rotatoria - Via Lombardia": { nodeId: null, roadIndex: 0 },
        "Via Bergamo": { nodeId: null, roadIndex: 0 },
        "SP525 Da Bergamo": { nodeId: null, roadIndex: 0 },
        "Via Combito": { nodeId: null, roadIndex: 0 },
        "SP525 Da Verdellino": { nodeId: null, roadIndex: 0 },
        "Via delle Reseghe": { nodeId: null, roadIndex: 0 }
      };

      // Search through all nodes to find the best match
      for (const node of dataJson.nodiAimsun) {
        if (node.codePerNodo && node.codePerNodo[1]?.codeMedie?.geometryCodaMedia) {
          const roads = node.codePerNodo[1].codeMedie.geometryCodaMedia;

          // Check if node name contains part of the section name
          const nodeName = node.name || "";

          // Try to match based on node name or location
          roads.forEach((road, index) => {
            if (!found) {
              // Check if section name contains node identifier or vice versa
              if (selectedSectionName.includes("Rotatoria") && node.tipo === "rotatoria") {
                setSelectedNodeId(node.id);
                setSelectedRoadIndex(index);
                found = true;
              } else if (selectedSectionName.includes("Via") || selectedSectionName.includes("SP")) {
                // For road sections, try to match by checking if it's part of the node's roads
                // Use the first available road from the node as a default
                setSelectedNodeId(node.id);
                setSelectedRoadIndex(index);
                found = true;
              }
            }
          });
        }

        if (found) break;
      }

      // If no match found but we have nodes, select the first road as a fallback
      if (!found && dataJson.nodiAimsun && dataJson.nodiAimsun.length > 0) {
        const firstNodeWithRoads = dataJson.nodiAimsun.find(node =>
          node.codePerNodo && node.codePerNodo[1]?.codeMedie?.geometryCodaMedia?.length > 0
        );

        if (firstNodeWithRoads) {
          setSelectedNodeId(firstNodeWithRoads.id);
          setSelectedRoadIndex(0);
        } else {
          setSelectedNodeId(null);
          setSelectedRoadIndex(null);
        }
      }
    } else {
      setSelectedNodeId(null);
      setSelectedRoadIndex(null);
    }
  }, [selectedSectionName, dataJson, selectedSectionId]);

  const handleNodeSelection = (index) => {
    const node = dataJson.nodiAimsun[index];
    if (node) {
      setSelectedNodeId(node.id);
    } else {
      setSelectedNodeId(null);
    }
  };

  const optionsOSM = { color: '#A3B2C9' };
  const optionCodaMax = { color: '#EC1D23' };
  const optionsCodaMedia = { color: '#FDB64E' };
  const optionsFer = {
    color: '#470d16b3',
    dashArray: '2, 10'
  };

  const pin = '/traffic.png';
  const pinMB = L.icon({
    iconUrl: pin,
    iconSize: [22, 22],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

  if (!dataJson || !dataJson.center) {
    return null;
  }

  return (
    <div style={{ height: "400px", width: "100%", marginTop: "20px" }}>
      <MapContainer
        key={`map-${dataJson.center[1]}-${dataJson.center[0]}`}
        center={[dataJson.center[1], dataJson.center[0]]}
        zoom={17}
        style={{ height: "100%", width: "100%" }}
        preferCanvas={true}
        zoomSnap={0}
        zoomDelta={0.5}
        wheelDebounceTime={0}
        wheelPxPerZoomLevel={120}
        zoomAnimation={false}
      >
        <TileLayer
          url={`https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_KEY}`}
          detectRetina={false}
          maxNativeZoom={50}
          maxZoom={50}
          minZoom={0}
          noWrap={false}
          opacity={1}
          subdomains="abc"
          tms={false}
        />
        <LegendControl />

        {/* Display nodes as polygons */}
        {dataJson.nodiAimsun && dataJson.nodiAimsun.map((item, index) => (
          <Polygon
            eventHandlers={{
              click: () => handleNodeSelection(index),
            }}
            key={item.id}
            pathOptions={{
              color: selectedNodeId === item.id ? '#1A4C3A' : '#8FC975',
            }}
            positions={item.geometryNodi.coordinates}
          />
        ))}

        {/* Display traffic light markers */}
        {selectedNodeId && dataJson.nodiAimsun && dataJson.nodiAimsun.map(node => {
          if (node.id === selectedNodeId && node.positionMarker && node.positionMarker.length === 2) {
            const markerPos = node.positionMarker;
            if (markerPos) {
              const parsed = [markerPos[0], markerPos[1]];
              return <Marker
                key={`marker-${node.id}`}
                position={parsed}
                icon={pinMB} />;
            }
          }
          return null;
        })}

        {/* Display OSM roads */}
        {dataJson.geometryOSM && dataJson.geometryOSM.length > 0 && dataJson.geometryOSM.filter(item => {
          const coords = item?.geometry || item;
          return Array.isArray(coords) && coords.length > 0;
        }).map((item, index) => {
          const coords = item?.geometry || item;
          return <Polyline key={`osm-${item?.id || index}`} pathOptions={optionsOSM} positions={coords} />;
        })}

        {/* Display Coda Max for selected node */}
        {dataJson && dataJson.nodiAimsun && dataJson.nodiAimsun.map(node => {
          if (node.id === selectedNodeId) {
            const geometryCodaMax = node.codePerNodo[0]?.codeMax?.geometryCodaMax;
            if (geometryCodaMax) {
              const parsedCoordinates = geometryCodaMax.map(coordinates =>
                coordinates.map(coordinate => [coordinate[0], coordinate[1]])
              );
              return <Polyline
                key={`coda-max-${node.id}`}
                pathOptions={optionCodaMax}
                positions={parsedCoordinates}
              />;
            }
          }
          return null;
        })}

        {/* Display all roads (Coda Media) with highlighting for selected one */}
        {dataJson.nodiAimsun && dataJson.nodiAimsun.map(node => {
          // Show all roads when no selection, or only roads for selected node
          if (selectedNodeId && selectedNodeId !== node.id) {
            return null;
          }

          return node.codePerNodo[1]?.codeMedie && node.codePerNodo[1]?.codeMedie.geometryCodaMedia &&
            node.codePerNodo[1]?.codeMedie.geometryCodaMedia.map((coordinates, index) => {
              // Determine if this road should be highlighted
              const isSelectedRoad = selectedSectionId &&
                                   selectedNodeId === node.id &&
                                   selectedRoadIndex === index;

              return (
                <Polyline
                  key={`coda-media-${node.id}-${index}`}
                  pathOptions={{
                    ...optionsCodaMedia,
                    weight: isSelectedRoad ? 6 : 3,
                    opacity: isSelectedRoad ? 1 : 0.6,
                    color: isSelectedRoad ? '#FF6B00' : optionsCodaMedia.color
                  }}
                  positions={coordinates}
                />
              );
            });
        })}
      </MapContainer>
    </div>
  );
};

export default SectionMap;